import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { SkeletonBlock } from '../Skeleton';
import type {
  Challenge,
  ChallengeType,
  ChallengeStatus,
  ChallengeStake,
  FriendsData,
  FriendCard,
} from '../../types/passport';

// ── ChallengeCard ─────────────────────────────────────────────────────────────

interface ChallengeCardProps {
  challenge: Challenge;
  currentUserId: string;
  onAccept: (id: string, stake: ChallengeStake) => void;
  onDecline: (id: string) => void;
  onCancel: (id: string) => void;
}

function ChallengeCard({ challenge, currentUserId, onAccept, onDecline, onCancel }: ChallengeCardProps) {
  const { t } = useTranslation();
  const isInitiator = challenge.initiatorId === currentUserId;
  const them = isInitiator ? challenge.opponent : challenge.initiator;
  const myScore = isInitiator ? challenge.initiatorScore : challenge.opponentScore;
  const theirScore = isInitiator ? challenge.opponentScore : challenge.initiatorScore;
  const [myStakeInput, setMyStakeInput] = useState(50);
  const isWinner = challenge.winnerId === currentUserId;
  const isLoser = !!challenge.winnerId && challenge.winnerId !== currentUserId;

  const typeLabel: Record<ChallengeType, string> = {
    RESTAURANTS: t('challenges.typeRestaurants'),
    ORDERS: t('challenges.typeOrders'),
    CATEGORIES: t('challenges.typeCategories'),
  };

  const daysLeft = Math.max(0, Math.ceil(
    (new Date(challenge.deadline).getTime() - Date.now()) / (1000 * 60 * 60 * 24)
  ));

  const stakeText = (s: ChallengeStake) =>
    s.type === 'points' ? `${s.amount} pts` : ` ${s.badgeName || 'Badge'}`;

  const statusLabel = (status: ChallengeStatus) => {
    switch (status) {
      case 'PENDING': return isInitiator ? t('challenges.waitingAccept') : t('challenges.incomingChallenge');
      case 'ACTIVE': return `${daysLeft}d ${t('challenges.left')}`;
      case 'COMPLETED': return isWinner ? ` ${t('challenges.won')}` : challenge.winnerId ? ` ${t('challenges.lost')}` : ` ${t('challenges.draw')}`;
      case 'DECLINED': return t('challenges.declined');
      default: return '';
    }
  };

  return (
    <div className={`pp-challenge-card pp-challenge-card--${challenge.status.toLowerCase()} ${isWinner ? 'pp-challenge-card--winner' : ''} ${isLoser ? 'pp-challenge-card--loser' : ''}`}>
      <div className="pp-challenge-header">
        <div className="pp-challenge-type">{typeLabel[challenge.type]}</div>
        <div className={`pp-challenge-status-badge pp-challenge-status--${challenge.status.toLowerCase()}`}>
          {statusLabel(challenge.status)}
        </div>
      </div>

      {['ACTIVE', 'COMPLETED'].includes(challenge.status) && (
        <div className="pp-challenge-score">
          <div className="pp-challenge-player">
            <div className="pp-challenge-player-name">{t('challenges.you')}</div>
            <div className="pp-challenge-player-score">{myScore}</div>
          </div>
          <div className="pp-challenge-vs">VS</div>
          <div className="pp-challenge-player">
            <div className="pp-challenge-player-name">{them.displayName}</div>
            <div className="pp-challenge-player-score">{theirScore}</div>
          </div>
        </div>
      )}

      <div className="pp-challenge-stakes">
        <div className="pp-challenge-stake">
          <span className="pp-challenge-stake-label">
            {isInitiator ? t('challenges.yourStake') : them.displayName}
          </span>
          <span className="pp-challenge-stake-value">{stakeText(challenge.initiatorStake)}</span>
        </div>
        {challenge.opponentStake && (
          <div className="pp-challenge-stake">
            <span className="pp-challenge-stake-label">
              {isInitiator ? them.displayName : t('challenges.yourStake')}
            </span>
            <span className="pp-challenge-stake-value">{stakeText(challenge.opponentStake)}</span>
          </div>
        )}
      </div>

      {challenge.status === 'PENDING' && isInitiator && (
        <button
          type="button"
          className="pp-challenge-cancel-btn"
          onClick={() => {
            if (window.confirm(t('challenges.confirmCancel', 'Отменить отправленный челлендж?'))) {
              onCancel(challenge.id);
            }
          }}
        >
          {t('challenges.cancel', 'Отменить')}
        </button>
      )}

      {challenge.status === 'PENDING' && !isInitiator && (
        <div className="pp-challenge-incoming">
          <div className="pp-challenge-incoming-hint">{t('challenges.yourCounterStake')}</div>
          <div className="pp-challenge-stake-input">
            <span>{t('challenges.points')}:</span>
            <input
              type="number"
              min={1}
              value={myStakeInput}
              onChange={e => setMyStakeInput(Number(e.target.value))}
              className="pp-challenge-points-input"
            />
          </div>
          <div className="pp-challenge-incoming-btns">
            <button
              className="pp-btn-accept"
              style={{ flex: 1, borderRadius: '10px', height: '40px' }}
              onClick={() => onAccept(challenge.id, { type: 'points', amount: myStakeInput })}
            >
               {t('challenges.accept')}
            </button>
            <button
              className="pp-btn-decline"
              style={{ flex: 1, borderRadius: '10px', height: '40px' }}
              onClick={() => onDecline(challenge.id)}
            >
               {t('challenges.decline')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── CreateChallengeModal ──────────────────────────────────────────────────────

interface CreateChallengeModalProps {
  friends: FriendCard[];
  currentPoints: number;
  onSubmit: (data: { opponentId: string; type: ChallengeType; deadlineDays: number; initiatorStake: ChallengeStake }) => void;
  onClose: () => void;
}

function CreateChallengeModal({ friends, currentPoints, onSubmit, onClose }: CreateChallengeModalProps) {
  const { t } = useTranslation();
  const [opponentId, setOpponentId] = useState(friends[0]?.id || '');
  const [type, setType] = useState<ChallengeType>('ORDERS');
  const [deadlineDays, setDeadlineDays] = useState(7);
  const [stakeAmount, setStakeAmount] = useState(50);

  const handleSubmit = () => {
    if (!opponentId || stakeAmount < 1 || stakeAmount > currentPoints) return;
    onSubmit({ opponentId, type, deadlineDays, initiatorStake: { type: 'points', amount: stakeAmount } });
  };

  return createPortal(
    <div className="pp-modal-overlay" onClick={onClose}>
      <div className="pp-modal pp-modal--challenge" onClick={e => e.stopPropagation()}>
        <div className="pp-modal-drag-bar" />
        <div className="pp-modal-header">
          <span className="pp-modal-title"> {t('challenges.create')}</span>
          <button type="button" className="pp-modal-close" onClick={onClose} aria-label="Close"></button>
        </div>

        <div className="pp-modal-body">
          <div className="pp-modal-section">
            <label className="pp-modal-label" htmlFor="ch-opponent-select">{t('challenges.chooseOpponent')}</label>
            <select
              id="ch-opponent-select"
              className="pp-modal-select"
              value={opponentId}
              onChange={(e) => setOpponentId(e.target.value)}
            >
              {friends.length === 0 && <option value="">{t('challenges.noFriendsYet')}</option>}
              {friends.map(f => (
                <option key={f.id} value={f.id}>{f.displayName}</option>
              ))}
            </select>
          </div>

          <div className="pp-modal-section">
            <label className="pp-modal-label">{t('challenges.chooseType')}</label>
            <div className="pp-modal-chips pp-modal-chips--type">
              {(['RESTAURANTS', 'ORDERS', 'CATEGORIES'] as ChallengeType[]).map(ct => (
                <button
                  key={ct}
                  type="button"
                  className={`pp-modal-chip pp-modal-chip--type ${type === ct ? 'pp-modal-chip--active' : ''}`}
                  onClick={() => setType(ct)}
                >
                  <span className="pp-modal-chip-emoji">
                    {ct === 'RESTAURANTS' && ''}
                    {ct === 'ORDERS' && ''}
                    {ct === 'CATEGORIES' && ''}
                  </span>
                  <span className="pp-modal-chip-text">
                    {ct === 'RESTAURANTS' && t('challenges.typeRestaurants')}
                    {ct === 'ORDERS' && t('challenges.typeOrders')}
                    {ct === 'CATEGORIES' && t('challenges.typeCategories')}
                  </span>
                </button>
              ))}
            </div>
          </div>

          <div className="pp-modal-section">
            <label className="pp-modal-label">{t('challenges.chooseDeadline')}</label>
            <div className="pp-modal-chips">
              {[3, 7, 14, 30].map(d => (
                <button
                  key={d}
                  type="button"
                  className={`pp-modal-chip pp-modal-chip--deadline ${deadlineDays === d ? 'pp-modal-chip--active' : ''}`}
                  onClick={() => setDeadlineDays(d)}
                >
                  {d} {t('challenges.days')}
                </button>
              ))}
            </div>
          </div>

          <div className="pp-modal-section">
            <label className="pp-modal-label">
              {t('challenges.yourStake')} <span className="pp-modal-label-hint">({t('challenges.youHave')} {currentPoints} pts)</span>
            </label>
            <div className="pp-modal-stake-wrap">
              <input
                type="number"
                min={1}
                max={currentPoints}
                value={stakeAmount}
                onChange={e => setStakeAmount(Number(e.target.value))}
                className="pp-modal-stake-input"
              />
              <span className="pp-modal-stake-suffix">pts</span>
            </div>
          </div>
        </div>

        <div className="pp-modal-footer">
          <button
            type="button"
            className="pp-modal-submit-btn"
            onClick={handleSubmit}
            disabled={stakeAmount < 1 || stakeAmount > currentPoints || !opponentId}
          >
            {t('challenges.sendChallenge')}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ── ChallengesView ────────────────────────────────────────────────────────────

interface ChallengesViewProps {
  challenges: Challenge[];
  challengesLoading: boolean;
  friendsData: FriendsData | null;
  effectiveCustomerId: string;
  showCreateChallenge: boolean;
  currentPoints: number;
  onSetShowCreateChallenge: (v: boolean) => void;
  onAcceptChallenge: (id: string, stake: ChallengeStake) => Promise<void>;
  onDeclineChallenge: (id: string) => Promise<void>;
  onCancelChallenge: (id: string) => Promise<void>;
  onCreateChallenge: (payload: { opponentId: string; type: ChallengeType; deadlineDays: number; initiatorStake: ChallengeStake }) => Promise<void>;
}

export default function ChallengesView({
  challenges,
  challengesLoading,
  friendsData,
  effectiveCustomerId,
  showCreateChallenge,
  currentPoints,
  onSetShowCreateChallenge,
  onAcceptChallenge,
  onDeclineChallenge,
  onCancelChallenge,
  onCreateChallenge,
}: ChallengesViewProps) {
  const { t } = useTranslation();

  return (
    <div className="pp-challenges-view pp-tab-content">
      {friendsData === null ? (
        // Friends still loading — show neutral skeleton-style placeholder, never "no friends" flash
        <div className="pp-challenge-create-btn" style={{ opacity: 0.5, pointerEvents: 'none' }}>
          {t('common.loading', 'Загрузка…')}
        </div>
      ) : friendsData.friends.length ? (
        <button className="pp-challenge-create-btn" onClick={() => onSetShowCreateChallenge(true)}>
           {t('challenges.create')}
        </button>
      ) : (
        <div className="pp-challenges-empty">
          <div></div>
          <div>{t('challenges.noFriendsYet')}</div>
        </div>
      )}

      {challengesLoading ? (
        <div className="pp-challenges-loading">
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="pp-challenge-card" style={{ pointerEvents: 'none' }}>
              <div className="pp-challenge-header">
                <SkeletonBlock width="110px" height="13px" radius="4px" />
                <SkeletonBlock width="68px" height="20px" radius="99px" />
              </div>
              <div className="pp-challenge-score">
                <div className="pp-challenge-player">
                  <SkeletonBlock width="48px" height="11px" radius="4px" style={{ margin: '0 auto 6px' }} />
                  <SkeletonBlock width="32px" height="22px" radius="6px" style={{ margin: '0 auto' }} />
                </div>
                <SkeletonBlock width="22px" height="14px" radius="4px" />
                <div className="pp-challenge-player">
                  <SkeletonBlock width="68px" height="11px" radius="4px" style={{ margin: '0 auto 6px' }} />
                  <SkeletonBlock width="32px" height="22px" radius="6px" style={{ margin: '0 auto' }} />
                </div>
              </div>
              <div className="pp-challenge-stakes">
                <div className="pp-challenge-stake">
                  <SkeletonBlock width="60%" height="11px" radius="4px" />
                  <SkeletonBlock width="40px" height="11px" radius="4px" />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : challenges.length === 0 ? (
        <div className="pp-challenges-empty">
          <div></div>
          <div>{t('challenges.empty')}</div>
        </div>
      ) : (
        challenges.map(ch => (
          <ChallengeCard
            key={ch.id}
            challenge={ch}
            currentUserId={effectiveCustomerId}
            onAccept={onAcceptChallenge}
            onDecline={onDeclineChallenge}
            onCancel={onCancelChallenge}
          />
        ))
      )}

      {showCreateChallenge && (
        <CreateChallengeModal
          friends={friendsData?.friends || []}
          currentPoints={currentPoints}
          onSubmit={onCreateChallenge}
          onClose={() => onSetShowCreateChallenge(false)}
        />
      )}
    </div>
  );
}
